import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TagChipInput.css';

interface TagChipInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  maxTags?: number;
  placeholder?: string;
  disabled?: boolean;
}

const DEFAULT_MAX = 5;

export const TagChipInput: React.FC<TagChipInputProps> = ({
  value,
  onChange,
  suggestions = [],
  maxTags = DEFAULT_MAX,
  placeholder,
  disabled = false,
}) => {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const atMax = value.length >= maxTags;

  const filtered = useMemo(() => {
    const q = draft.trim().toLowerCase();
    const taken = new Set(value.map(v => v.toLowerCase()));
    return suggestions
      .filter(s => !taken.has(s.toLowerCase()))
      .filter(s => !q || s.toLowerCase().includes(q))
      .slice(0, 8);
  }, [draft, suggestions, value]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const commit = (raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    if (value.some(v => v.toLowerCase() === tag.toLowerCase())) return;
    if (value.length >= maxTags) return;
    onChange([...value, tag]);
    setDraft('');
  };

  const remove = (tag: string) => {
    onChange(value.filter(v => v !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault();
      remove(value[value.length - 1]);
    }
  };

  return (
    <div className="tag-chip-input" ref={wrapperRef}>
      <div className={`tag-chip-input__field${disabled ? ' is-disabled' : ''}`}>
        {value.map(tag => (
          <span key={tag} className="tag-chip-input__chip">
            {tag}
            {!disabled && (
              <button
                type="button"
                className="tag-chip-input__chip-remove"
                aria-label={`Remove ${tag}`}
                onClick={() => remove(tag)}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!disabled && !atMax && (
          <input
            type="text"
            className="tag-chip-input__entry"
            value={draft}
            onChange={e => { setDraft(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
          />
        )}
      </div>
      <div className="tag-chip-input__meta">
        {value.length}/{maxTags}
      </div>
      {open && filtered.length > 0 && (
        <ul className="tag-chip-input__suggestions" role="listbox">
          {filtered.map(s => (
            <li key={s}>
              <button
                type="button"
                className="tag-chip-input__suggestion"
                onMouseDown={e => { e.preventDefault(); commit(s); }}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TagChipInput;
